import React from 'react';
import CodeBlock from './CodeBlock';

// Grounded on backend schemas in app/schemas/error.py
const ErrorDocs = () => {
  return (
    <section className="section">
      <h2 className="section-title">Errors</h2>
      <p className="text-gray-300 mb-6 leading-7 max-w-[70ch]">
        Errors follow FastAPI conventions with a standard <code>detail</code> field. Some endpoints may return lists
        of validation errors. Rate limits are enforced and return 429 responses. See examples below.
      </p>

      <div className="p-3 bg-gray-900 border border-gray-800 rounded mb-6 text-xs text-gray-400">
        <strong className="text-gray-300">Note:</strong> Responses include <code>X-Request-ID</code> header for tracing
        (set by middleware). Include this ID when reporting issues.
      </div>

      <div className="card mb-6">
        <h3 className="text-lg font-semibold text-gray-200 mb-2">Standard Error</h3>
        <p className="text-sm text-gray-400 mb-2">Generic error response with a string detail.</p>
        <CodeBlock code={`{
  "detail": "Error message here"
}`} />
      </div>

      <div className="card mb-6">
        <h3 className="text-lg font-semibold text-gray-200 mb-2">401 Unauthorized</h3>
        <p className="text-sm text-gray-400 mb-2">Missing or invalid API key.</p>
        <CodeBlock code={`{
  "detail": "API Key required"
}`} />
      </div>

      <div className="card mb-6">
        <h3 className="text-lg font-semibold text-gray-200 mb-2">401 Unauthorized (Missing API Key)</h3>
        <p className="text-sm text-gray-400 mb-2">Returned when API key is not provided.</p>
        <CodeBlock code={`{
  "detail": "API Key required"
}`} />
      </div>

      <div className="card mb-6">
        <h3 className="text-lg font-semibold text-gray-200 mb-2">403 Forbidden (Not Authorized for Namespace)</h3>
        <p className="text-sm text-gray-400 mb-2">Returned when the user lacks access to a namespace.</p>
        <CodeBlock code={`{
  "detail": "Not authorized to access this namespace"
}`} />
      </div>

      <div className="card mb-6">
        <h3 className="text-lg font-semibold text-gray-200 mb-2">403 Forbidden</h3>
        <p className="text-sm text-gray-400 mb-2">Provided API key could not be validated.</p>
        <CodeBlock code={`{
  "detail": "Could not validate credentials"
}`} />
      </div>

      <div className="card mb-6">
        <h3 className="text-lg font-semibold text-gray-200 mb-2">404 Not Found</h3>
        <CodeBlock code={`{
  "detail": "Resource not found"
}`} />
      </div>

      <div className="card mb-6">
        <h3 className="text-lg font-semibold text-gray-200 mb-2">422 Validation Error</h3>
        <p className="text-sm text-gray-400 mb-2">Validation errors return a list detailing the error locations and messages.</p>
        <CodeBlock code={`{
  "detail": [
    {
      "loc": ["body", "email"],
      "msg": "value is not a valid email address",
      "type": "value_error.email"
    }
  ]
}`} />
      </div>

      <div className="card mb-6">
        <h3 className="text-lg font-semibold text-gray-200 mb-2">429 Too Many Requests</h3>
        <p className="text-sm text-gray-400 mb-2">Rate limit exceeded. Includes the limit window configured by the server.</p>
        <CodeBlock code={`{
  "detail": "Rate limit exceeded: 100 per 1 minute"
}`} />
        <p className="text-xs text-gray-500 mt-2">Use exponential backoff and check Retry-After if provided.</p>
      </div>

      <div className="card mb-6">
        <h3 className="text-lg font-semibold text-gray-200 mb-2">503 Service Unavailable</h3>
        <p className="text-sm text-gray-400 mb-2">Health endpoint may return structured details when dependencies are down.</p>
        <CodeBlock code={`{
  "detail": {
    "status": "unhealthy",
    "timestamp": "2024-01-01T00:00:00Z",
    "services": {
      "postgresql": {
        "status": "unhealthy",
        "connected": false,
        "error": "Connection error"
      }
    }
  }
}`} />
      </div>

      <div className="card mb-6">
        <h3 className="text-lg font-semibold text-gray-200 mb-2">503 Service Unavailable (Neo4j disabled)</h3>
        <p className="text-sm text-gray-400 mb-2">Returned by knowledge-graph endpoints when Neo4j is disabled.</p>
        <CodeBlock code={`{
  "detail": "Neo4j is not enabled"
}`} />
      </div>

      <div className="card mb-6">
        <h3 className="text-lg font-semibold text-gray-200 mb-2">500 Internal Server Error</h3>
        <p className="text-sm text-gray-400 mb-2">Unexpected server errors include a descriptive message.</p>
        <CodeBlock code={`{
  "detail": "Error querying graph: <message>"
}`} />
      </div>
    </section>
  );
};

export default ErrorDocs;
